import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Copy, Check, ArrowRight } from "lucide-react";
import { api, currency } from "@/lib/api";
import { APPLIANCES } from "@/lib/appliances";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const inputClass =
  "w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white placeholder:text-[#6B7E92] focus:border-[#F26A21] focus:outline-none focus:ring-2 focus:ring-[#F26A21]/30 transition-all duration-300";

export default function GetCustomerID() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    plan_slug: params.get("plan") || "",
    appliances: [],
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const toggleAppliance = (a) => {
    setForm((f) => ({
      ...f,
      appliances: f.appliances.includes(a)
        ? f.appliances.filter((x) => x !== a)
        : [...f.appliances, a],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const { name, email, phone, address, city, plan_slug } = form;
    if (!name || !email || !phone || !address || !city || !plan_slug) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/customers", form);
      setResult(data);
      toast.success("Welcome to ASA. Your Customer ID is ready.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(result.customer_id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div data-testid="get-id-page" className="min-h-[70vh]">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left */}
        <div className="px-5 sm:px-8 lg:px-16 pt-14 md:pt-20 pb-20">
          <p className="overline text-[#F26A21] mb-5">Register</p>
          <h1 className="font-heading font-bold text-5xl sm:text-6xl tracking-tight text-white leading-[1.05]">
            Get your{" "}
            <span className="text-[#F26A21]">Customer ID.</span>
          </h1>

          <AnimatePresence mode="wait">
            {!result ? (
              <motion.form
                key="form"
                onSubmit={submit}
                data-testid="register-form"
                className="mt-10 space-y-5 max-w-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-[#8CA0B4] block mb-2">Full name *</label>
                    <input data-testid="reg-name" className={inputClass} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="overline text-[#8CA0B4] block mb-2">Phone *</label>
                    <input data-testid="reg-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                  </div>
                </div>

                <div>
                  <label className="overline text-[#8CA0B4] block mb-2">Email *</label>
                  <input data-testid="reg-email" type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>

                <div>
                  <label className="overline text-[#8CA0B4] block mb-2">Address *</label>
                  <input data-testid="reg-address" className={inputClass} value={form.address} onChange={set("address")} placeholder="Flat / house, street" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="overline text-[#8CA0B4] block mb-2">City *</label>
                    <input data-testid="reg-city" className={inputClass} value={form.city} onChange={set("city")} placeholder="City" />
                  </div>
                  <div>
                    <label className="overline text-[#8CA0B4] block mb-2">Plan *</label>
                    <Select value={form.plan_slug} onValueChange={(v) => setForm((f) => ({ ...f, plan_slug: v }))}>
                      <SelectTrigger data-testid="reg-plan" className="w-full h-14 bg-[#0C1C2E] border border-[#223851] rounded-xl px-4 text-white focus:border-[#F26A21] data-[placeholder]:text-[#6B7E92]">
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#122539] border-[#223851] text-white">
                        {plans.map((p) => (
                          <SelectItem key={p.slug} value={p.slug} data-testid={`plan-opt-${p.slug}`}>
                            {p.name} — ₹{currency(p.price)}/{p.period}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="overline text-[#8CA0B4] block mb-3">Appliances to cover</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {APPLIANCES.map((a) => (
                      <label
                        key={a}
                        data-testid={`reg-appliance-${a}`}
                        className="flex items-center gap-2 rounded-xl border border-[#223851] px-3 py-3 cursor-pointer hover:border-[#F26A21] transition-colors"
                      >
                        <Checkbox
                          checked={form.appliances.includes(a)}
                          onCheckedChange={() => toggleAppliance(a)}
                          className="border-[#6B7E92] data-[state=checked]:bg-[#F26A21] data-[state=checked]:border-[#F26A21]"
                        />
                        <span className="text-xs text-[#D3DEE8]">{a}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit"
                  className="w-full h-14 bg-[#F26A21] text-white rounded-full font-semibold flex items-center justify-center gap-2 hover:bg-[#DE5C15] transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Generate my Customer ID"}
                </button>
              </motion.form>
            ) : (
              <motion.div
                key="success"
                data-testid="register-success"
                className="mt-10 max-w-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <p className="text-[#AEBECD] leading-relaxed">
                  You're all set, {result.name.split(" ")[0]}. Save this Customer ID —
                  use it anytime on the Customer Lookup page to track your plan and
                  service visits.
                </p>

                <div className="mt-7 rounded-3xl border border-[#F26A21] bg-[#122539] p-7">
                  <p className="overline text-[#6B7E92] mb-3">Your Customer ID</p>
                  <div className="flex items-center justify-between gap-4">
                    <span
                      data-testid="generated-customer-id"
                      className="font-mono font-semibold text-4xl sm:text-5xl text-[#F26A21]"
                    >
                      {result.customer_id}
                    </span>
                    <button
                      onClick={copyId}
                      data-testid="copy-id-btn"
                      className="shrink-0 rounded-xl border border-[#223851] p-3 text-white hover:border-[#F26A21] hover:text-[#F26A21] transition-colors"
                      aria-label="Copy Customer ID"
                    >
                      {copied ? <Check size={18} /> : <Copy size={18} />}
                    </button>
                  </div>
                  <div className="mt-6 pt-6 border-t border-[#223851] grid grid-cols-2 gap-4">
                    <div>
                      <p className="overline text-[#6B7E92]">Plan</p>
                      <p className="text-white mt-1">{result.plan_name}</p>
                    </div>
                    <div>
                      <p className="overline text-[#6B7E92]">Next service</p>
                      <p className="font-mono text-white mt-1">{result.next_service_date}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-7">
                  <button
                    onClick={() => navigate(`/lookup?id=${result.customer_id}`)}
                    data-testid="goto-lookup"
                    className="inline-flex items-center gap-2 bg-[#F26A21] text-white px-6 py-3.5 rounded-full font-semibold hover:bg-[#DE5C15] transition-colors group"
                  >
                    View my dashboard
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                  <Link to="/plans" className="inline-flex items-center gap-2 border border-[#33506e] px-6 py-3.5 rounded-full font-semibold text-white hover:border-[#F26A21] hover:text-[#F26A21] transition-colors">
                    Back to plans
                  </Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right panel */}
        <div className="hidden lg:flex sticky top-20 h-[calc(100vh-5rem)] bg-[#0A1626] border-l border-[#223851] items-center justify-center p-16 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(242,106,33,0.10),transparent_60%)]" />
          <div className="relative">
            <p className="overline text-[#F26A21] mb-6">Membership · ASA</p>
            <p className="font-heading font-bold text-6xl leading-[1.05] tracking-tight text-white">
              One ID.
              <br />
              <span className="text-[#F26A21]">Every</span>
              <br />
              appliance.
            </p>
            <p className="text-[#AEBECD] mt-8 max-w-sm leading-relaxed">
              Your Customer ID unlocks unlimited scheduled services, priority
              repairs and a full service history — all in one place.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
